import React, { useState, useEffect } from "react";
import { authService } from "../../services/authService";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";

interface ResetPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    resetKey: string;
    onPasswordReset: () => void;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
    isOpen,
    onClose,
    resetKey,
    onPasswordReset,
}) => {
    const [password, setPassword] = useState("");
    const [retypePassword, setRetypePassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setShowPassword(false);
            setShowRetypePassword(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!password) {
            setError("Please enter a new password");
            return;
        }

        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }

        if (password !== retypePassword) {
            setError("Passwords do not match");
            return;
        }

        setIsLoading(true);
        try {
            await authService.resetPassword(resetKey, password);
            setPassword("");
            setRetypePassword("");
            onPasswordReset();
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to reset password. Please try again.";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setPassword("");
        setRetypePassword("");
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={handleCancel}
            title="Type new password"
            isLoading={isLoading}
        >
            <form onSubmit={handleSubmit}>
                {/* Password */}
                <div className="mb-5 relative">
                    <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError(null);
                        }}
                        placeholder="Password"
                        className={`${POPUP_STYLES.input} ${error ? "bg-red-50 border-2 border-red-400" : ""} pr-12`}
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        disabled={isLoading}
                    >
                        {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Retype Password */}
                <div className="mb-5 relative">
                    <input
                        type={showRetypePassword ? "text" : "password"}
                        value={retypePassword}
                        onChange={(e) => {
                            setRetypePassword(e.target.value);
                            setError(null);
                        }}
                        placeholder="Retype Password"
                        className={`${POPUP_STYLES.input} ${error ? "bg-red-50 border-2 border-red-400" : ""} pr-12`}
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        onClick={() => setShowRetypePassword(!showRetypePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        disabled={isLoading}
                    >
                        {showRetypePassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-5 p-3 bg-red-50 border border-red-300 text-red-800 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Buttons */}
                <div className="flex gap-4 mt-6">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isLoading}
                        className={`flex-1 ${POPUP_STYLES.button}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading || !password || !retypePassword}
                        className={`flex-1 ${POPUP_STYLES.button}`}
                    >
                        {isLoading ? "Resetting..." : "OK"}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
};
